import { validationIssue } from '@dinewise/i18n';
import * as z from 'zod';
import { disposableEmailBlocklist } from './disposable-email-blocklist.ts';

export const emailSchema = z.string().trim().email();

export const isAddressableEmail = (value: string): boolean => {
  const angleBracketed = /<([^<>]*)>/.exec(value);
  const address = (angleBracketed?.[1] ?? value).trim();

  if (address.includes('<') || address.includes('>')) {
    return false;
  }

  const atIndex = address.indexOf('@');
  if (atIndex === -1) {
    return false;
  }

  const localPart = address.slice(0, atIndex).trim();
  const domainPart = address.slice(atIndex + 1).trim();

  return localPart.length > 0 && domainPart.length > 0;
};

// https://email-verify.my-addr.com/list-of-most-popular-email-domains.php + some additions
const personalEmailDomains = [
  'aim.com',
  'alice.it',
  'aliceadsl.fr',
  'aol.com',
  'arcor.de',
  'att.net',
  'bellsouth.net',
  'bigpond.com',
  'bigpond.net.au',
  'bluewin.ch',
  'blueyonder.co.uk',
  'bol.com.br',
  'centurytel.net',
  'charter.net',
  'chello.nl',
  'club-internet.fr',
  'comcast.net',
  'cox.net',
  'earthlink.net',
  'free.fr',
  'freenet.de',
  'frontiernet.net',
  'gmail.com',
  'gmx.de',
  'gmx.net',
  'googlemail.com',
  'hetnet.nl',
  'hey.com',
  'home.nl',
  'hotmail.co.uk',
  'hotmail.com',
  'hotmail.de',
  'hotmail.es',
  'hotmail.fr',
  'hotmail.it',
  'icloud.com',
  'ig.com.br',
  'juno.com',
  'laposte.net',
  'libero.it',
  'live.ca',
  'live.co.uk',
  'live.com.au',
  'live.com',
  'live.fr',
  'live.it',
  'live.nl',
  'mac.com',
  'mail.com',
  'mail.ru',
  'me.com',
  'msn.com',
  'neuf.fr',
  'ntlworld.com',
  'optonline.net',
  'optusnet.com.au',
  'orange.fr',
  'outlook.com',
  'planet.nl',
  'protonmail.com',
  'qq.com',
  'rambler.ru',
  'rediffmail.com',
  'rocketmail.com',
  'sbcglobal.net',
  'sfr.fr',
  'shaw.ca',
  'sky.com',
  'skynet.be',
  'sympatico.ca',
  't-online.de',
  'telenet.be',
  'terra.com.br',
  'tin.it',
  'tiscali.co.uk',
  'tiscali.it',
  'uol.com.br',
  'verizon.net',
  'virgilio.it',
  'voila.fr',
  'wanadoo.fr',
  'web.de',
  'windstream.net',
  'yahoo.ca',
  'yahoo.co.id',
  'yahoo.co.in',
  'yahoo.co.jp',
  'yahoo.co.uk',
  'yahoo.com.ar',
  'yahoo.com.au',
  'yahoo.com.br',
  'yahoo.com.mx',
  'yahoo.com.sg',
  'yahoo.com',
  'yahoo.de',
  'yahoo.es',
  'yahoo.fr',
  'yahoo.in',
  'yahoo.it',
  'yandex.ru',
  'ymail.com',
  'zonnet.nl',
];
const companyDomainAllowList = ['gofundme.com'];

const personalEmailDomainsSet = new Set([...personalEmailDomains, ...disposableEmailBlocklist]);

export const isPersonalOrDisposableEmailDomain = (email: string): boolean => {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) {
    return false;
  }

  // check exact match
  if (personalEmailDomainsSet.has(domain)) {
    return true;
  }

  // check subdomains
  for (const personalDomain of personalEmailDomainsSet) {
    if (domain.endsWith(`.${personalDomain}`)) {
      return true;
    }
  }

  return false;
};

// The message is named rather than inlined so a per-request error map can
// localize it. A literal `message` here would outrank every error map, since
// Zod resolves schema-level messages before the parse-level and global ones.
export const companyEmailSchema = emailSchema.refine(
  email =>
    companyDomainAllowList.some(domain => email.endsWith(domain)) ||
    !isPersonalOrDisposableEmailDomain(email),
  {
    params: validationIssue('email.company'),
  },
);

const internalAndeDomains = ['ande.ai', 'andehospitality.com'];

const domainIsOrUnder = (domain: string, parent: string) =>
  domain === parent || domain.endsWith(`.${parent}`);

export const normalizeVenueGroupDomain = (value: string): string => {
  const domain = value.trim().toLowerCase().replace(/^@/, '').replace(/\.$/, '');
  if (
    domain.length === 0 ||
    !domain.includes('.') ||
    domain.includes(' ') ||
    domain.includes('/') ||
    domain.includes('@')
  ) {
    throw new Error('Enter a valid domain like example.com');
  }

  return domain;
};

export const isBlockedVenueGroupDomain = (domain: string): boolean => {
  const normalized = domain.trim().toLowerCase().replace(/^@/, '');
  if (internalAndeDomains.some(blocked => domainIsOrUnder(normalized, blocked))) {
    return true;
  }

  return isPersonalOrDisposableEmailDomain(`person@${normalized}`);
};

export const emailMatchesDomain = (email: string, domain: string): boolean => {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) {
    return false;
  }

  const emailDomain = email
    .slice(atIndex + 1)
    .trim()
    .toLowerCase();
  const normalizedDomain = domain.trim().toLowerCase().replace(/^@/, '');

  return emailDomain.length > 0 && emailDomain === normalizedDomain;
};

export const getEmailDomain = (email: string) => {
  const [_, domain] = emailSchema.parse(email).split('@');

  if (!domain) {
    throw new Error('Invalid email');
  }

  return domain;
};

type ThreadUsers = 'USER' | 'VENUE' | 'SUPPORT';

/**
 * Generates the plusAddress of an email address for a reply-to address,
 * where the plusAddress is the content after the `+` and before the `@` symbols
 *
 * @returns The generated local-part of the email address.
 */
export const generateReplyToPlusAddress = (source: ThreadUsers, threadReferenceCode: string) => {
  const prefix = source === 'SUPPORT' ? 'S' : source === 'VENUE' ? 'V' : 'U';

  return `${prefix}${threadReferenceCode}`;
};

export const parseEmailAddressFromHeader = (value: string): string | null => {
  const angleBracketed = /<([^<>]*)>/.exec(value);
  const address = (angleBracketed?.[1] ?? value).trim().toLowerCase();
  const parsed = emailSchema.safeParse(address);

  return parsed.success ? parsed.data : null;
};

export const parseReplyToAddress = (emailAddress: string) => {
  const [localPart] = emailAddress.split('@');
  if (!localPart) {
    return null;
  }
  const plusAddress = localPart.split('+')[1];

  if (!plusAddress) {
    return null;
  }

  const normalizedPlusAddress = plusAddress.toUpperCase();

  const prefix = normalizedPlusAddress[0];
  const threadReferenceCode = normalizedPlusAddress.slice(1);

  return {
    source: prefix === 'S' ? 'SUPPORT' : prefix === 'V' ? 'VENUE' : 'USER',
    threadReferenceCode,
  };
};

const VENUE_AGENT_REPLY_TOKEN_LENGTH = 26;

export const formatVenueAgentEscalationReplyToAddress = ({
  agentLocalPart,
  replyToken,
  domain,
}: {
  agentLocalPart: string;
  replyToken: string;
  domain: string;
}) => `${agentLocalPart}+${replyToken}@${domain}`;

export const parseVenueAgentEscalationReplyAddress = (
  emailAddress: string,
  agentLocalPart: string,
): { replyToken: string } | null => {
  const trimmedAddress = emailAddress.trim();
  const atIndex = trimmedAddress.indexOf('@');
  if (atIndex === -1) {
    return null;
  }

  const localPart = trimmedAddress.slice(0, atIndex);
  const plusIndex = localPart.indexOf('+');
  if (plusIndex === -1) {
    return null;
  }

  const baseLocalPart = localPart.slice(0, plusIndex);
  if (baseLocalPart.toLowerCase() !== agentLocalPart.toLowerCase()) {
    return null;
  }

  const replyToken = localPart.slice(plusIndex + 1).toUpperCase();
  if (replyToken.length !== VENUE_AGENT_REPLY_TOKEN_LENGTH) {
    return null;
  }

  return { replyToken };
};

export const findVenueAgentEscalationReplyFromDestinations = (
  destinationEmails: string[],
  agentLocalPart: string,
): { replyToken: string } | null => {
  for (const destinationEmail of destinationEmails) {
    const address = parseEmailAddressFromHeader(destinationEmail) ?? destinationEmail;
    const parsed = parseVenueAgentEscalationReplyAddress(address, agentLocalPart);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

export const isPersonalEmailDomain = (domain: string): boolean => {
  return personalEmailDomainsSet.has(domain);
};

export const isPersonalEmail = (email: string): boolean => {
  try {
    const parsedEmail = emailSchema.parse(email.toLowerCase().trim());
    const domain = parsedEmail.split('@').pop();
    if (!domain) {
      return false;
    }

    return isPersonalEmailDomain(domain);
  } catch {
    return false;
  }
};
